















































































































import { clone } from '@/jbi-shared/util/group-level-attributes.util';
import {
  AddressCountry,
  AddressFilterOption,
  AddressState,
  VenueFieldData
} from '@/store/modules/admin/types/group-level-attribute.types';
import { RootState } from '@/store/store';
import { ApiState } from '@/store/types/general.types';
import {
  Vue,
  Component,
  Prop,
  Emit,
  Watch,
  PropSync
} from 'vue-property-decorator';
import { Action, State } from 'vuex-class';
import { ToastProgrammatic as Toast } from 'buefy';

@Component({})
export default class AddressVenueFields extends Vue {
  // --- PROPS ---
  // Props for v-model binding with parent component fields
  @Prop({ type: Object, required: true })
  readonly value!: VenueFieldData;
  @Prop({ type: Boolean, default: false })
  readonly isRequired!: boolean;
  @Prop({ type: Boolean, default: false })
  readonly disabled!: boolean;
  @Prop({ type: Boolean, default: false })
  readonly isSettings!: boolean;
  @Prop({ type: Boolean, default: false })
  readonly isLocation!: boolean;

  // Props to handle optional field rendering
  @PropSync('enableBuildingName')
  readonly enableBuildingNameSync!: boolean;

  @PropSync('enableLineTwo')
  readonly enableLineTwoSync!: boolean;

  // --- ACTIONS AND STATE VARIABLES ---
  @Action('admin/getCountries')
  getAllCountries!: (addressFilter?: AddressFilterOption) => void;

  @Action('admin/getCountryByCountryId')
  getCountryById!: (countryId?: number) => void;

  @State(({ admin }: RootState) => admin.apiState.getCountries)
  getAllCountriesApiState!: ApiState;

  @State(({ admin }: RootState) => admin.apiState.getCountryById)
  getCountryByIdApiState!: ApiState;

  @State(({ admin }: RootState) => admin.countries)
  allCountries!: AddressCountry[] | undefined;

  @State(({ admin }: RootState) => admin.countryById)
  countryById!: AddressCountry | undefined;

  lineOneErrorMsg: string = '';
  cityErrorMsg: string = '';
  postcodeErrorMsg: string = '';
  stateErrorMsg: string = '';
  countryErrorMsg: string = '';

  lineOneFieldError: boolean = false;
  cityFieldError: boolean = false;
  postcodeFieldError: boolean = false;
  countryFieldError: boolean = false;
  stateFieldError: boolean = false;
  linkFieldError: boolean = false;

  currentCountry: AddressCountry | undefined = undefined;
  currentStates: AddressState[] = [];
  // Used to determine whether to store fetched states
  selectedCountryId: number = 0;
  isStateless: boolean = true;

  get countriesList(): AddressCountry[] {
    if (this.allCountries) {
      return this.allCountries.filter((country) => {
        return country.name
          .toLowerCase()
          .includes((this.value.country as string).toLowerCase());
      });
    }

    return [];
  }

  get statesList(): AddressState[] {
    if (this.currentStates.length) {
      return this.currentStates.filter((state) => {
        return state.name
          .toLowerCase()
          .includes((this.value.state as string).toLowerCase());
      });
    }

    return [];
  }

  created() {
    if (this.allCountries === undefined) {
      this.getAllCountries();
    }
  }

  mounted() {
    const hasValues = Object.entries(this.value).some(
      ([key, value]) => value !== ''
    );

    if (this.isSettings && hasValues) {
      this.validateAllFields();
      this.handleCountryPreselection();
    }
  }

  handleCountryPreselection(): void {
    // Fetch country details if a country is pre-selected
    if (this.value.country && this.allCountries) {
      this.validateCountry(); // Trigger fetch if needed
    } else if (this.value.country) {
      // If countries aren't loaded yet, watch for them
      const unwatch = this.$watch('allCountries', (newCountries) => {
        if (newCountries && newCountries.length > 0) {
          this.validateCountry();
          unwatch(); // Stop watching once loaded
        }
      });
    }
  }

  resetValidation(): void {
    this.lineOneErrorMsg = '';
    this.cityErrorMsg = '';
    this.postcodeErrorMsg = '';
    this.stateErrorMsg = '';
    this.countryErrorMsg = '';
    this.emitErrorStatus(false);
  }

  validateVenueFields(): void {
    this.validateLineOne();
    this.validateCity();
    this.validatePostcode();
    this.validateCountry();
    this.validateState();
  }

  validateAllFields() {
    this.resetValidation();
    this.validateVenueFields();
  }

  validateLineOne(value = this.value.lineOne): void {
    if (this.isRequired && !value) {
      this.lineOneErrorMsg = `Address Line 1 is required.`;
      this.lineOneFieldError = true;
      return;
    }

    this.updateField('lineOne', value);
    this.lineOneErrorMsg = '';
    this.lineOneFieldError = false;
  }

  validateCity(value = this.value.city): void {
    if (this.isRequired && !value) {
      this.cityErrorMsg = `City is required.`;
      this.cityFieldError = true;
      return;
    }

    this.updateField('city', value);
    this.cityErrorMsg = '';
    this.cityFieldError = false;
  }

  validatePostcode(value = this.value.postcode): void {
    if (this.isRequired && !value) {
      this.postcodeErrorMsg = `Postcode is required.`;
      this.postcodeFieldError = true;
      return;
    }

    this.updateField('postcode', value);
    this.postcodeErrorMsg = '';
    this.postcodeFieldError = false;
  }

  validateCountry(value = this.value.country): void {
    if (!this.allCountries) {
      this.countryErrorMsg = '';
      // Do not reset dependents here, wait for data
      this.countryFieldError = false;
      return;
    }

    /**
     * Early return on missing value, if country becomes empty, reset states
     * and re-verify state (which will clear field error too)
     */
    if (!value) {
      this.countryErrorMsg = this.isRequired ? `Country is required.` : '';
      this.resetCountryStateDependents();
      this.validateState();
      this.countryFieldError = true;
      return;
    }

    // Continue validation for non-empty values
    const targetCountry = this.allCountries.find(
      (country) => country.name.toLowerCase() === value.toLowerCase()
    );

    // Case 1: Invalid country
    if (!targetCountry) {
      this.countryErrorMsg = 'Invalid country name.';
      this.resetCountryStateDependents();
      this.validateState();
      this.countryFieldError = true;
      return;
    }

    // Case 2: Valid country
    this.countryErrorMsg = '';
    const previousSelectedCountryId = this.selectedCountryId;
    this.selectedCountryId = targetCountry.id;
    this.updateField('country', targetCountry.name);
    this.countryFieldError = false;

    // Fetch states only if the country ID actually changed
    if (this.selectedCountryId !== previousSelectedCountryId) {
      this.getCountryById(this.selectedCountryId);
    }

    // Always re-verify state after country is validated (or changed)
    this.validateState();
  }

  validateState(value = this.value.state): void {
    /**
     * Guard clause 1:
     * Ensure field is active and associated country has states.
     * Also ensures `currentStates` is available (should be if not stateless, but check defensively)
     * on the off-chance API call has not returned data.
     */
    if (
      this.isStateless ||
      !this.currentStates ||
      this.currentStates.length === 0
    ) {
      this.stateErrorMsg = '';
      this.stateFieldError = false;
      return;
    }

    // Early return on missing value
    if (!value) {
      this.stateErrorMsg = `State is required.`;
      this.stateFieldError = true;
      return;
    }

    // Continue validation for non-empty values
    const targetState = this.currentStates.find(
      (state) => state.name.toLowerCase() === value.toLowerCase()
    );

    // Case 1: Invalid state
    if (!targetState) {
      this.stateErrorMsg = 'Invalid state name.';
      this.stateFieldError = true;
      return;
    }

    // Case 2: Valid state
    this.stateErrorMsg = '';
    this.updateField('state', targetState.name);
    this.stateFieldError = false;
  }

  // Helper to reset state fields dependent on the country
  resetCountryStateDependents(): void {
    this.selectedCountryId = 0;
    this.isStateless = true;
    this.currentStates = [];
    // Clear local state value only if it currently has a value to avoid unnecessary watcher triggers/emits
    if (this.value.state) {
      this.updateField('state', '');
    }
  }

  updateField(field: keyof VenueFieldData, value: string): void {
    this.onInputChanged(field, value);
  }

  assignCountryValue(selectedCountry: AddressCountry | null): void {
    const value = selectedCountry ? selectedCountry.name : '';
    this.validateCountry(value);
  }

  assignStateValue(selectedState: AddressState | null): void {
    const value = selectedState ? selectedState.name : '';
    this.validateState(value);
  }

  @Emit('input')
  onInputChanged(field: keyof VenueFieldData, value: string) {
    return {
      ...this.value,
      [field]: value
    };
  }

  @Emit('update:has-error')
  emitErrorStatus(hasError: boolean) {
    return hasError;
  }

  /**
   * Watcher to trigger validation. Find which field changed and validate only that one
   * Note: Country and State have validation triggered in `assignCountryValue` and `assignStateValue`,
   * so it is excluded here.
   */
  @Watch('value', { deep: true })
  onValueChange(newValue: VenueFieldData, oldValue: VenueFieldData): void {
    if (newValue.lineOne !== oldValue.lineOne) {
      this.validateLineOne(newValue.lineOne);
    }

    if (newValue.city !== oldValue.city) {
      this.validateCity(newValue.city);
    }

    if (newValue.postcode !== oldValue.postcode) {
      this.validatePostcode(newValue.postcode);
    }

    if (newValue.country !== oldValue.country) {
      this.validateCountry(newValue.country);
    }

    if (newValue.state !== oldValue.state) {
      this.validateState(newValue.state);
    }
  }

  @Watch('isRequired')
  onIsRequiredChange(newIsRequired: boolean): void {
    const hasValues = Object.entries(this.value).some(
      ([key, value]) => value !== ''
    );

    if (newIsRequired && !hasValues) {
      // From false to true: Set parent error to true, but DON'T run validation yet.
      // This prevents showing errors before the user has a chance to type.
      this.emitErrorStatus(true);
    } else {
      // From true to false: Reset all validation.
      this.resetValidation();
    }
  }

  // --- Watcher to validate overall error state ---
  @Watch('lineOneFieldError')
  @Watch('cityFieldError')
  @Watch('postcodeFieldError')
  @Watch('countryFieldError')
  @Watch('stateFieldError')
  @Watch('linkFieldError')
  onIndividualFieldErrorStates(): void {
    const hasFieldError: boolean =
      this.lineOneFieldError ||
      this.cityFieldError ||
      this.postcodeFieldError ||
      this.countryFieldError ||
      this.stateFieldError ||
      this.linkFieldError;

    this.emitErrorStatus(hasFieldError);
  }

  // --- Watchers for country and state dropdowns ---
  @Watch('getAllCountriesApiState.error')
  @Watch('getCountryByIdApiState.error')
  addressApiErrorCallback(): void {
    Toast.open('Error fetching countries and states.');
  }

  @Watch('getAllCountriesApiState.success')
  validateCountryCallback(): void {
    // Clear state value when there's error with country name
    this.validateCountry();
    if (this.countryErrorMsg) {
      this.updateField('state', '');
    }
  }

  @Watch('getCountryByIdApiState.success')
  validateStateCallback(): void {
    if (
      this.countryById &&
      (this.selectedCountryId === 0 ||
        this.selectedCountryId === this.countryById.id)
    ) {
      this.currentCountry = clone(this.countryById) as AddressCountry;
      this.currentStates = clone(this.countryById.states) as AddressState[];
      this.isStateless = this.currentStates.length === 0;
      // Re-validate state after fetching states
      this.validateState();
    }
  }
}
